import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiX } from 'react-icons/fi';
import { DataRoomFolder } from '../../types/dataRoom';

// Type cast icons for React 18 compatibility
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

interface AddFolderModalProps {
  onClose: () => void;
  onSubmit: (name: string, parentFolderId?: string) => Promise<void>;
  folders?: DataRoomFolder[];
  defaultParentId?: string;
}

const AddFolderModal: React.FC<AddFolderModalProps> = ({
  onClose,
  onSubmit,
  folders,
  defaultParentId,
}) => {
  const [name, setName] = useState('');
  const [parentFolderId, setParentFolderId] = useState<string>(defaultParentId || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only show top-level folders as parent options
  const topLevelFolders = (folders || []).filter(f => !f.parent_folder_id);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Folder name is required');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(name.trim(), parentFolderId || undefined);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create folder');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modal = (
    <div className="su-dataroom-modal-overlay" onClick={handleOverlayClick}>
      <div className="su-dataroom-modal" style={{ maxWidth: '400px' }} role="dialog" aria-modal="true" aria-labelledby="add-folder-title">
        <div className="su-dataroom-modal-header">
          <div>
            <h2 id="add-folder-title" className="su-dataroom-modal-title">
              {defaultParentId ? 'Add Sub-folder' : 'Add Folder'}
            </h2>
          </div>
          <button
            className="su-dataroom-modal-close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="su-dataroom-modal-body">
            {error && (
              <div
                style={{
                  color: '#f87171',
                  fontSize: '14px',
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                {error}
              </div>
            )}

            <div className="su-dataroom-form-group">
              <label htmlFor="folder-name" className="su-dataroom-label">Folder Name</label>
              <input
                id="folder-name"
                type="text"
                className="su-dataroom-input"
                placeholder="e.g., Cap Table"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            {topLevelFolders.length > 0 && !defaultParentId && (
              <div className="su-dataroom-form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="parent-folder" className="su-dataroom-label">Parent Folder (optional)</label>
                <select
                  id="parent-folder"
                  className="su-dataroom-input"
                  value={parentFolderId}
                  onChange={(e) => setParentFolderId(e.target.value)}
                >
                  <option value="">None (top-level folder)</option>
                  {topLevelFolders.map(folder => (
                    <option key={folder.id} value={folder.id}>{folder.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="su-dataroom-modal-footer">
            <button
              type="button"
              className="su-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="su-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : (defaultParentId ? 'ADD SUB-FOLDER' : 'ADD FOLDER')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default AddFolderModal;
